import { IRefIds } from 'constants/refs';
import { get, post } from 'services/request';
import { BaseModel, IModelOptions } from './BaseModel';
import { IConceptLinkerValidatorItem } from 'types/conceptLinkerValidator';
import { IModelUpdateRowTypes } from '../types/models';

export default class ConceptLinkerValidatorItemsModel extends BaseModel<IConceptLinkerValidatorItem, null> {
  validationId?: number;

  constructor(options: IModelOptions & { validationId?: number }) {
    super(options);
    this.validationId = options.validationId;
    this.ref = IRefIds.conceptLinkerValidator;
    this.gridName = 'ConceptLinkerValidatorItems';
    this.getGridColumns();
  }

  setValidationId(validationId: number) {
    this.validationId = validationId;
    this.gridData = [];
    this.getGrid();
  }

  async getGrid() {
    if (!this.validationId) return;

    await super.getGrid();
    this.gridData = await get<IConceptLinkerValidatorItem[]>(`concept-linker-validator/${this.validationId}/items`);
    this.loadingGrid = false;
    this.render();
  }

  async keepItem(itemId: number) {
    const response = await post<{
      success: boolean;
      message: string;
      item_id: number;
      status: number;
      decision: string;
    }>(`concept-linker-validator/items/${itemId}/keep`, {});

    if (response.data) {
      // Update just the affected row
      await this.updateGridRow(itemId, 'update');
    }

    return response;
  }

  async changeItem(itemId: number) {
    const response = await post<{
      success: boolean;
      message: string;
      item_id: number;
      status: number;
      decision: string;
    }>(`concept-linker-validator/items/${itemId}/change`, {});

    if (response.data) {
      // Update just the affected row
      await this.updateGridRow(itemId, 'update');
    }

    return response;
  }

  async unlinkItem(itemId: number) {
    const response = await post<{
      success: boolean;
      message: string;
      item_id: number;
      status: number;
      decision: string;
    }>(`concept-linker-validator/items/${itemId}/unlink`, {});

    if (response.data) {
      // Update just the affected row
      await this.updateGridRow(itemId, 'update');
    }

    return response;
  }

  async refresh() {
    await this.getGrid();
  }

  async reload() {
    await super.reload();
    await this.getGrid();
  }

  async loadItemData(itemId: number) {
    return {};
  }

  async getGridRow(itemId: number) {
    return await get<IConceptLinkerValidatorItem>(`concept-linker-validator/items/${itemId}`);
  }

  async updateGridRow(itemId: number, type: IModelUpdateRowTypes = 'update') {
    // Call parent implementation
    await super.updateGridRow(itemId, type);

    // Also update the gridData array to keep counts in sync
    if (type === 'update' && this.gridDataUpdateRow?.data && Array.isArray(this.gridData)) {
      const index = this.gridData.findIndex((item) => item.id === itemId);
      if (index !== -1) {
        this.gridData[index] = this.gridDataUpdateRow.data as IConceptLinkerValidatorItem;
        this.gridData = [...this.gridData];
        // Force immediate render to update summary
        this.render();
      }
    } else if (type === 'remove' && Array.isArray(this.gridData)) {
      this.gridData = this.gridData.filter((item) => item.id !== itemId);
    }
  }

  async bulkChange() {
    this.loadingGrid = true;
    this.render();
    const response = await post<{
      success: boolean;
      message: string;
      processed: number;
      errors: Array<{ item_id: number; error: string }>;
    }>(`concept-linker-validator/${this.validationId}/bulk-change`, {});

    if (response.data) {
      // Refresh the entire grid after bulk operation
      await this.getGrid();
    }

    this.loadingGrid = false;
    this.render();

    return response;
  }

  async bulkUnlink() {
    this.loadingGrid = true;
    this.render();
    const response = await post<{
      success: boolean;
      message: string;
      processed: number;
      errors: Array<{ item_id: number; error: string }>;
    }>(`concept-linker-validator/${this.validationId}/bulk-unlink`, {});

    if (response.data) {
      // Refresh the entire grid after bulk operation
      await this.getGrid();
    }

    this.loadingGrid = false;
    this.render();

    return response;
  }

  async bulkKeep() {
    this.loadingGrid = true;
    this.render();
    const response = await post<{
      success: boolean;
      message: string;
      processed: number;
      errors: Array<{ item_id: number; error: string }>;
    }>(`concept-linker-validator/${this.validationId}/bulk-keep`, {});

    if (response.data) {
      // Refresh the entire grid after bulk operation
      await this.getGrid();
    }

    this.loadingGrid = false;
    this.render();

    return response;
  }
}
